import React, { forwardRef, useImperativeHandle, useRef, useState, useMemo, useCallback } from 'react'
import { Form, Spin, Tooltip } from 'antd';
import { FieldTypes, generateFormFields, urlRegexValidator } from 'utils/formUtils';
import BaseModal from 'components/BaseModal';
import FindingAPI from 'restAPI/FindingAPI';
import _, { snakeCase, sortBy } from 'lodash';
import AppLoader from 'components/AppLoader';
import message from 'components/CustomMessage';
import { MarkdownFormatter } from "components/MarkupFormatter";
import { getMessageEditorFields } from 'views/Findings/ManualFindingPopup';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import { getCVSSField } from 'views/Findings/ManualFindingsCVSSFields';
import htmlToMarkDown, { sanitizeMarkdown } from 'utils/htmlToMarkDownUtil';
import { marked } from 'marked';
import { MANUAL_FINDING_MARK_DOWN_FIELDS, TOOL_FINDING_CATEGORIES, scanTypeOptions } from './Constants';
import axios from 'axios';
import { MODAL_SIZE_EXTRA_LARGE, MODAL_SIZE_LARGE } from 'constants/Layout';
import { removeNonPrintableUnicodeCharacters } from 'utils/fileUtils';
import { getCvssScoreFromVector } from 'utils/cvssUtil';
import { parseCWEOptions } from 'views/DraftFindingForm/DraftFindingFormMetaDataSection';
import { InfoIcon } from 'components/Icons';
import { convertCommentToNewFormat } from './FindingDetails/common';

const KEY_MAPPING = {
    cvssScore: 'baseScore',
    exploited: 'additionalDetails.exploited'
}

const MitigationModal = forwardRef(({finding, afterClose, fieldType="mitigation", isCustomFinding}, ref) => {
    const thisRef = useRef();
    useImperativeHandle(ref, () => ({ ...thisRef.current }));
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const editorRef = useRef({});
    const { id: findingId } = finding || {};
    const [initialValues] = useState(() => {
        let values = _.get(finding, KEY_MAPPING[fieldType] || fieldType);
        if (fieldType === 'componentFixVersions') {
            values = _.get(finding, 'additionalDetails.componentFixVersions');
        }
        
        // convert markdown to html
        if (MANUAL_FINDING_MARK_DOWN_FIELDS.includes(fieldType) && values) {
            values = convertCommentToNewFormat(marked.parse(values));
        }

        return {scope: "FINDING", [fieldType]: values, notes: ''}
    })
    const pathParamRef = useRef({title: '', path: ''})

    const saveEditedField = useCallback(async (payload = {}) => {
        let path = pathParamRef.current.path; 
        const requestPayload = { ...payload };
        if (['componentName', 'componentVersion', 'componentFixVersions'].includes(pathParamRef.current.path)) {
            path = snakeCase(pathParamRef.current.path);
            if (requestPayload.componentFixVersions === '') {
                requestPayload.componentFixVersions = null;
            }
        }
        await FindingAPI.updateFindingField(findingId, path, requestPayload).promise;
    },[findingId]);

    const handleOnOk = useCallback(async () => {
        await form.validateFields();
            const data = form.getFieldsValue();
            if (['cves', 'cwes'].includes(fieldType)) {
                const val = data[fieldType] ? data[fieldType]: [];
                data[fieldType] = fieldType === 'cwes' ? _.map(val, entry => +entry) : val;
            }

            // convert html to markdown
            if (MANUAL_FINDING_MARK_DOWN_FIELDS.includes(fieldType)) {
                if (data[fieldType]) {
                    const value = data[fieldType];
                    data[fieldType] = sanitizeMarkdown(htmlToMarkDown(value));
                }
            }
            const payload = {
                ...data,
                findingId
            }
            if(fieldType === 'cvssVector') {
                payload.cvssScore = getCvssScoreFromVector(data.cvssVector);
            }
            // setLoading(true);
            console.log(payload)
            await saveEditedField(payload);
            setLoading(false);
            message.success("Updated Successfully") 
    },[findingId, form, saveEditedField, fieldType]);

    const getCWEOptions = useCallback(
        (params) => {
          const { promise, cancel } = FindingAPI.getCWEs(params);
          return {
            cancel,
            promise: promise.then((response) => {
              return [...response.content, ...(initialValues.cwes || [])].sort();
            }),
          };
        },
        [initialValues]
      );

    const getField = useCallback(() => {
        const fields = [];
        const editorFields = getMessageEditorFields(editorRef);
        const dyField = editorFields.find(field => field.name === fieldType);
        let path = 'finding';
        let title = ''
        switch(fieldType) {
            case "mitigation":
                // old api --> /user/findings/findingRemediation/
                // new api --> /user/findings/findingMitigation/
                title = getVocabulary("mitigation");
                fields.push(dyField);
                path += "Mitigation";
            break;
            case 'description': 
            case 'stepsToReproduce':
            case 'impact':
                title = title ? title : _.startCase(fieldType);
                fields.push(dyField);
                path += title.replace(/ +/g, "");
            break;
            case 'ruleKey':
                title = 'AMC Rules';
                fields.push({
                    name: fieldType,
                    label: title,
                    type: FieldTypes.SELECT,
                    props: {
                        mode: 'tags',
                        allowClear: true,
                        autoFocus: true
                    }
                })
                path += 'AMCRules';
            break;
            case 'toolId':
                title = 'Tool Finding ID';
                fields.push({
                    name: fieldType,
                    label: title,
                    props: {
                        autoFocus: true
                    }
                })
                path += 'ToolFindingId';
            break;
            case 'findingCategory':
                title = <>Tool Finding <Vocabulary name="category" /></>;
                fields.push({
                    name: fieldType,
                    label: title,
                    type: FieldTypes.SELECT,
                    options: TOOL_FINDING_CATEGORIES,
                    colSpan: 24,
                    props: {
                        allowClear: true,
                        autoFocus: true
                    }
                })
                path += 'ToolFindingCategory'
            break;
            case 'cves':
                title = 'CVE';
                fields.push({
                    name: fieldType,
                    label: title,
                    type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
                    notFoundContent: "No CVE Vulnerability is present",
                    props: {
                        mode: 'multiple',
                        api: FindingAPI.getCVEs,
                        allowClear: true,
                        autoFocus: true
                    }
                })
                path += 'Cves';
            break;
            case 'cwes':
                title = 'CWE';
                fields.push({
                    name: fieldType,
                    label: <>{title}
                        {
                            !isCustomFinding && <Tooltip title="After updating the CWE list within ArmorCode, any updates from the source tool will be ignored"><InfoIcon className="m-l-xs" /></Tooltip>
                        }
                    </>,
                    placeholder: title,
                    type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
                    notFoundContent: "No CWE Vulnerability is present",
                    props: {
                        mode: 'multiple',
                        api: getCWEOptions,
                        parseSelectOptions: parseCWEOptions,
                        optionsMutator: (options) => sortBy(options, "value"),
                        allowClear: true,
                        autoFocus: true
                    }
                })
                path += 'Cwes';
            break;
            case 'cvssVector': {
                title = 'CVSS Vector';
                const [cvssVector] = getCVSSField()
                fields.push({...cvssVector, colSpan: 24, type: FieldTypes.CVSS_VECTOR, rules: [], required: false, props: {form}})
                path += 'CvssVector';
            break;
            }
            case 'cvssScore': {
                title = 'CVSS Score';
                const cvssScore = getCVSSField()[1];
                fields.push({...cvssScore, colSpan: 24, props: { autoFocus: true }})
                path += 'CvssScore'
            break;
            }
            case 'scanType':
                title = "Scan Type";
                fields.push({
                    name: fieldType,
                    label: title,
                    type: FieldTypes.SELECT,
                    options: scanTypeOptions,
                    colSpan: 24,
                    props: {
                        allowClear: true,
                        autoFocus: true
                    }
                })
                path += 'ScanType'
            break;
            case 'title':
                title = "Summary";
                fields.push({
                    name: fieldType,
                    label: title,
                    type: FieldTypes.TEXT_AREA,
                    colSpan: 24,
                    props: {
                        autoFocus: true
                    }
                })
                path += 'title'
            break;
            case 'newUrl':
                title = "Endpoint";
                fields.push({
                    name: fieldType,
                    label: title,
                    colSpan: 24,
                    props: {
                        autoFocus: true
                    },
                    rules:[urlRegexValidator]
                })
                path += 'UrlEndpoint'
            break;
            case 'exploited':
                title = "Exploit Available";
                fields.push({
                    name: fieldType,
                    label: title,
                    required: true,
                    type: FieldTypes.SELECT,
                    options: [{ label: "True", value: true }, { label: "False", value: false }],
                    colSpan: 24,
                    props: {
                        autoFocus: true
                    }
                })
                path += 'exploited'
            break;
            case 'componentName':
                title = "Component Name";
                fields.push({
                    name: fieldType,
                    label: title,
                    colSpan: 24,
                    props: {
                        autoFocus: true
                    },
                })
                path = 'componentName'
            break;
            case 'componentVersion':
                title = "Component Affected Version";
                fields.push({
                    name: fieldType,
                    label: title,
                    colSpan: 24,
                    props: {
                        autoFocus: true
                    },
                })
                path = 'componentVersion'
            break;
            case 'componentFixVersions':
                title = "Component Fix Version";
                fields.push({
                    name: fieldType,
                    label: title,
                    colSpan: 24,
                    props: {
                        autoFocus: true
                    },
                })
                path = 'componentFixVersions'
            break;
            default:break;
        }
        pathParamRef.current = {path, title};
        return fields;

    }, [fieldType, isCustomFinding, getCWEOptions, form])

    const fields = useMemo(() => {
        return [
            {
                name: "summary",
                label: "Summary",
                type: FieldTypes.LABEL,
                value: <MarkdownFormatter markdown={finding.title} collapsible={true} />,
            },
            ...getField(),
            {
                name: "notes",
                label: "Note",
                type: FieldTypes.TEXT_AREA,
                required: true,
                customLengthRule: {
                    max: 1000,
                    message: "Maximum length is 1000 characters"
                },
            }
        ]
    }, [finding.title, getField])

    const modalProps = {
        title: <>Edit {pathParamRef.current.title}</>,
        onOk: handleOnOk,
        okText: "Submit",
        width: ["mitigation", "description", "stepsToReproduce", "impact"].includes(fieldType) ? MODAL_SIZE_EXTRA_LARGE : fieldType === 'cvssVector' ? MODAL_SIZE_LARGE : undefined,
        className: "modal-full-height no-footer"
    }

    return (
        <BaseModal
            modalProps={modalProps}
            ref={thisRef}
            afterClose={afterClose}
        >
            <Spin spinning={loading} indicator={<AppLoader />}>
                <Form
                    layout="vertical"
                    form={form}
                    initialValues={initialValues}
                >
                    {generateFormFields(fields)}
                </Form>
            </Spin>
        </BaseModal>
    )
})

export default MitigationModal

export const uploadFileForEditor = async (file) => {
    if (file) {
        const fileName = removeNonPrintableUnicodeCharacters(file.name);
        const { id, signedUrl } = await FindingAPI.uploadManualAttachment({ fileName }).promise;
        await axios({ method: 'put', url: signedUrl, data: file });
        const params = new URLSearchParams({attachmentId: id, filename: fileName });
        const url = `/user/tools/generic/CUSTOM/attachment?${params.toString()}`;
        return url;
    }
};
